import { endpoints } from '@api'
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react'
import { useExperienceManager } from '@hooks'
import { trackEvent } from '@lib/clinician-tracking'
import {
  CardNumberElement,
  Elements,
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useStoreState } from 'easy-peasy'
import React, { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useHistory } from 'react-router-dom'
import type { StoreModel } from 'src/store/types'
import {
  getOrganizationControllerGetSessionCreditQueryKey,
  useClinicControllerGetPaymentMethods,
  useOrganizationControllerChangePlan,
  useOrganizationControllerGetSessionCredit,
  useOrganizationControllerPurchaseSessionCredits
} from '~/clinician-api'
import type { ChangePlanRequestDtoNewPlanType } from '~/clinician-api/models'
import {
  CreditCardForm,
  SavedPaymentMethod
} from '../../settings/update-your-plan/components'
import { AutoReplenish } from '../components/auto-replenish'
import {
  PlanCard,
  type PlanData,
  useSessionPlans
} from './components/PlanOptions'
import { PurchaseSummary } from './components/PurchaseSummary'
import { SessionExchange } from './components/SessionExchange'

// @ts-ignore
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY)

// Interface for future props if needed
// interface ManagePlanProps {}

const ManagePlanContent = () => {
  const [selectedPlan, setSelectedPlan] = useState('plus')
  const [sessionCount, setSessionCount] = useState<number | undefined>(
    undefined
  )
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null)
  const [isPaymentMethodComplete, setIsPaymentMethodComplete] =
    useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  // Local auto-replenish settings (used for free plan users, mirrors API settings for paid users)
  const [localAutoReplenishEnabled, setLocalAutoReplenishEnabled] =
    useState<boolean>(false)
  const [localAutoReplenishThreshold, setLocalAutoReplenishThreshold] =
    useState<number | null>(5)
  const [localAutoReplenishCount, setLocalAutoReplenishCount] = useState<
    number | null
  >(30)

  const stripe = useStripe()
  const elements = useElements()
  const toast = useToast()
  const history = useHistory()
  const queryClient = useQueryClient()

  // Get user to access organization ID
  const { user } = useStoreState((state: StoreModel) => state.auth)
  const { usageBasedPricingEnabled } = useExperienceManager()

  const { mutateAsync: purchaseSessions } =
    useOrganizationControllerPurchaseSessionCredits()

  const { mutateAsync: changePlan } = useOrganizationControllerChangePlan()
  // Get session credits data
  const { data: sessionCredits, isLoading: isLoadingSessionCredits } =
    useOrganizationControllerGetSessionCredit(
      user?.clinic?.organization_id || '',
      {
        query: {
          enabled: !!(usageBasedPricingEnabled && user?.clinic?.organization_id)
        }
      }
    )

  const { plans } = useSessionPlans()
  const clinicId = user?.clinic?.id

  const { data: paymentMethods = [], isLoading: isPaymentMethodsLoading } =
    useClinicControllerGetPaymentMethods(clinicId || '', {
      query: {
        enabled: !!clinicId
      }
    })

  const defaultPaymentMethod = paymentMethods.find(p => p.isDefault)

  useEffect(() => {
    if (defaultPaymentMethod) {
      setSelectedPaymentMethodId(defaultPaymentMethod.id)
    }
  }, [defaultPaymentMethod])

  // Handle auto-replenish changes from the AutoReplenish component
  const handleAutoReplenishChange = (
    enabled: boolean,
    threshold: number | null,
    replenishCount: number | null
  ) => {
    setLocalAutoReplenishEnabled(enabled)
    setLocalAutoReplenishThreshold(threshold)
    setLocalAutoReplenishCount(replenishCount)
  }

  // Set default selected plan based on current plan
  useEffect(() => {
    if (sessionCredits?.planType) {
      const currentPlanType = sessionCredits.planType.toLowerCase()
      if (['standard', 'plus', 'pro'].includes(currentPlanType)) {
        setSelectedPlan(currentPlanType)
      }
    }
  }, [sessionCredits])

  // Initialize local auto-replenish settings from session credits
  useEffect(() => {
    if (sessionCredits) {
      setLocalAutoReplenishEnabled(sessionCredits.autoReplenishEnabled ?? false)
      setLocalAutoReplenishThreshold(sessionCredits.autoReplenishThreshold ?? 5)
      setLocalAutoReplenishCount(sessionCredits.autoReplenishCount ?? 30)
    }
  }, [sessionCredits])

  // Auto-enable and set auto-replenish for free users when session count changes
  useEffect(() => {
    const isFreePlan = !sessionCredits || sessionCredits?.planType === 'free'

    if (isFreePlan && sessionCount && sessionCount >= 30) {
      setLocalAutoReplenishEnabled(true)
      setLocalAutoReplenishCount(sessionCount)
      // Keep threshold at default if not already set
      if (!localAutoReplenishThreshold) {
        setLocalAutoReplenishThreshold(5)
      }
    }
  }, [sessionCount, sessionCredits, localAutoReplenishThreshold])

  // Early return after all hooks are called
  if (!clinicId) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Text>No clinic ID found</Text>
      </Flex>
    )
  }

  const getSelectedPlanPrice = () => {
    const plan = plans.find((p: PlanData) => p.id === selectedPlan)
    if (!plan || !plan.price) return 0

    return plan.price
  }

  const getSelectedPlanTitle = () => {
    const plan = plans.find((p: PlanData) => p.id === selectedPlan)
    return plan ? `${plan.title}` : 'Starter'
  }

  const getSelectedPlanPriceId = () => {
    const plan = plans.find((p: PlanData) => p.id === selectedPlan)
    return plan?.priceId || null
  }

  // Determine current plan from session credits
  const getCurrentPlan = () => {
    if (!sessionCredits) {
      return 'No active plan'
    }

    const { planType } = sessionCredits

    // Determine if user is on free trial based on having only free credits
    const isFreeTrial = !sessionCredits || sessionCredits?.planType === 'free'

    // Format plan name to match Blueprint naming convention
    const formattedPlan = `${planType.charAt(0).toUpperCase() + planType.slice(1)}`

    // Add free trial suffix if applicable
    return isFreeTrial ? `Starter` : formattedPlan
  }

  // Calculate session conversion between plans
  const calculateSessionConversion = () => {
    if (!sessionCredits || !plans.length) return null

    const currentPlanType = sessionCredits.planType?.toLowerCase()
    const newPlanType = selectedPlan

    // Don't show conversion for same plan or free plan
    if (
      currentPlanType === newPlanType ||
      currentPlanType === 'free' ||
      !currentPlanType
    ) {
      return null
    }

    const currentPlan = plans.find((p: PlanData) => p.id === currentPlanType)
    const newPlan = plans.find((p: PlanData) => p.id === newPlanType)

    if (!currentPlan || !newPlan || !currentPlan.price || !newPlan.price) {
      return null
    }

    // Only show conversion if user has any credits
    if (sessionCredits.creditsRemaining <= 0) {
      return null
    }

    // Calculate conversion rate: (current plan price / new plan price)
    const conversionRate =
      Math.round(currentPlan.price * 10) /
      10 /
      (Math.round(newPlan.price * 10) / 10)

    // Convert both regular and free credits separately
    const convertedTotalCredits = Math.ceil(
      sessionCredits.creditsRemaining * conversionRate
    )
    const convertedFreeCredits = Math.ceil(
      sessionCredits.freeCreditsRemaining * conversionRate
    )

    return {
      currentPlan: `${currentPlan.title}`,
      currentSessions: sessionCredits.creditsRemaining,
      currentFreeCredits: sessionCredits.freeCreditsRemaining,
      newPlan: `${newPlan.title}`,
      convertedSessions: convertedTotalCredits,
      convertedFreeCredits: convertedFreeCredits
    }
  }

  const createStripePaymentMethod = async (): Promise<string | null> => {
    const cardElement = elements?.getElement(CardNumberElement)

    if (!cardElement) {
      console.error('Card Element not available')
      return null
    }

    if (!stripe) {
      console.error('Stripe not available')
      return null
    }

    const {
      // @ts-ignore
      paymentMethod,
      // @ts-ignore
      paymentMethodError
      // @ts-ignore
    } = await stripe.createPaymentMethod({ type: 'card', card: cardElement })

    if (paymentMethodError) {
      toast({
        title: 'Error saving credit card. Please Try again.',
        status: 'error',
        isClosable: true,
        duration: 3000
      })
      return null
    }

    if (!paymentMethod) {
      console.error('No payment method created')
      return null
    }

    return paymentMethod.id as string
  }

  const handlePurchase = async () => {
    setIsSubmitting(true)

    try {
      // Check if this is a plan switch (conversion)
      const conversion = calculateSessionConversion()
      const isConversion = conversion !== null
      const hasAdditionalPurchase = sessionCount && sessionCount >= 30
      const isOnFreePlan = sessionCredits?.planType === 'free'

      // Always use local settings which are synced from API for paid users
      const shouldEnableAutoReplenish = localAutoReplenishEnabled
      const effectiveAutoReplenishThreshold = localAutoReplenishEnabled
        ? localAutoReplenishThreshold || 5
        : null
      const effectiveAutoReplenishCount = localAutoReplenishEnabled
        ? localAutoReplenishCount || sessionCount || 30
        : null

      // Step 1: Handle plan conversion if applicable (for switching between paid plans)
      if (isConversion && conversion && sessionCredits) {
        await changePlan({
          organizationId: user?.clinic?.organization_id || '',
          data: {
            newPlanType: selectedPlan as ChangePlanRequestDtoNewPlanType
          }
        })

        if (!hasAdditionalPurchase) {
          toast({
            title: 'Plan Changed Successfully',
            description: `Your ${
              conversion.currentSessions + conversion.currentFreeCredits
            } ${conversion.currentPlan} sessions have been converted to ${
              conversion.convertedSessions + conversion.convertedFreeCredits
            } ${conversion.newPlan} sessions`,
            status: 'success',
            isClosable: true,
            duration: 5000
          })
        }
      }

      // Step 2: Handle additional session purchase if applicable
      if (hasAdditionalPurchase) {
        // Use existing saved payment method if available, otherwise create new one
        let paymentMethodId = selectedPaymentMethodId

        if (!paymentMethodId) {
          // No saved payment method, create one from the card input
          paymentMethodId = await createStripePaymentMethod()
        }

        if (!paymentMethodId) {
          throw new Error('Payment method is required')
        }

        const priceId = getSelectedPlanPriceId()

        if (!priceId) {
          throw new Error('No price ID found for selected plan')
        }

        await purchaseSessions({
          organizationId: user?.clinic?.organization_id || '',
          data: {
            stripePriceId: priceId,
            quantity: sessionCount,
            stripePaymentMethodId: paymentMethodId,
            autoReplenishEnabled: shouldEnableAutoReplenish,
            autoReplenishThreshold: effectiveAutoReplenishThreshold,
            autoReplenishCount: effectiveAutoReplenishCount
          }
        })

        // Show appropriate success message
        if (isConversion && conversion) {
          toast({
            title: 'Plan Changed & Sessions Purchased',
            description: `Your ${
              conversion.currentSessions + conversion.currentFreeCredits
            } ${conversion.currentPlan} sessions have been converted to ${
              conversion.convertedSessions + conversion.convertedFreeCredits
            } ${conversion.newPlan} sessions, and ${sessionCount} additional ${getSelectedPlanTitle()} sessions have been purchased`,
            status: 'success',
            isClosable: true,
            duration: 5000
          })
        } else if (isOnFreePlan) {
          toast({
            title: 'Plan Upgraded Successfully',
            description: `You've been upgraded to the ${getSelectedPlanTitle()} plan and purchased ${sessionCount} sessions`,
            status: 'success',
            isClosable: true,
            duration: 3000
          })

          trackEvent('Clinician Upgraded to Paid', {
            planType: getSelectedPlanTitle(),
            sessionCount: sessionCount
          })
        } else {
          toast({
            title: 'Sessions Purchased Successfully',
            description: `You've purchased ${sessionCount} ${getSelectedPlanTitle()} sessions`,
            status: 'success',
            isClosable: true,
            duration: 3000
          })
        }
      }

      // Invalidate caches after all operations are complete
      await queryClient.invalidateQueries(
        endpoints.getPaywallValidation.getCacheId()
      )

      const sessionCreditsQueryKey =
        getOrganizationControllerGetSessionCreditQueryKey(
          user?.clinic?.organization_id || ''
        )

      await queryClient.invalidateQueries(sessionCreditsQueryKey)

      // Redirect to sessions page
      history.push('/sessions')
    } catch (error) {
      console.error('Purchase error:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Error processing request'
      const errorDescription =
        error instanceof Error && 'error' in error
          ? (error as { error?: { description?: string } }).error
              ?.description || 'Please try again or contact support'
          : 'Please try again or contact support'

      toast({
        title: errorMessage,
        description: errorDescription,
        status: 'error',
        isClosable: true,
        duration: 5000
      })
    }

    setIsSubmitting(false)
  }

  return (
    <Box w="100%" bg="#F7F8FE" minH="100vh" position="relative">
      <Box
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg="#F7F8FE"
        zIndex="-1"
        pointerEvents="none"
      />
      <Flex
        maxW="1200px"
        justify="center"
        gap={{ base: '0', md: '8' }}
        px="6"
        py="6"
        mx="auto"
        direction={{ base: 'column', md: 'row' }}
        position="relative"
        zIndex="1"
      >
        <Box
          bg="white"
          borderRadius="lg"
          p="6"
          flex="1"
          maxW={{ base: '100%', md: '65%' }}
          w="100%"
          pb={{ base: '6', md: '16' }}
        >
          <Box mb={{ base: '6', md: '8' }}>
            <Heading
              mb="2"
              fontSize={{ base: '20px', md: '24px' }}
              fontWeight="bold"
              color="black"
            >
              Manage Plan
            </Heading>
            <Text color="gray.600" fontSize={{ base: '14px', md: '16px' }}>
              Choose your plan and purchase sessions to continue using Blueprint
            </Text>
          </Box>

          <VStack spacing="8" align="stretch">
            {/* Step 1: Choose your plan */}
            <Box>
              <Flex
                align="center"
                gap={{ base: '2', md: '3' }}
                mb={{ base: '4', md: '6' }}
              >
                <Box
                  w={{ base: '20px', md: '24px' }}
                  h={{ base: '20px', md: '24px' }}
                  bg="#F6F7FE"
                  color="#2D54E8"
                  marginRight={{ base: '8px', md: '10px' }}
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize={{ base: '12px', md: '14px' }}
                  fontWeight="bold"
                  flexShrink={0}
                >
                  1
                </Box>
                <Heading
                  size="md"
                  fontSize={{ base: '16px', md: '18px' }}
                  fontWeight="600"
                  color="black"
                >
                  Choose your plan
                </Heading>
              </Flex>
              <SimpleGrid
                columns={{ base: 1, sm: 3 }}
                spacing={{ base: '3', md: '4' }}
              >
                {plans.map((plan: PlanData) => {
                  return (
                    <PlanCard
                      key={plan.id}
                      title={plan.title}
                      price={plan.price}
                      features={plan.features}
                      isRecommended={plan.isRecommended}
                      isSelected={selectedPlan === plan.id}
                      onClick={() =>
                        !plan.isComingSoon && setSelectedPlan(plan.id)
                      }
                      isComingSoon={plan.isComingSoon}
                      showCurrentTrial={
                        (!sessionCredits ||
                          sessionCredits?.planType === 'free') &&
                        plan.id === 'plus'
                      }
                      showCurrentPlan={
                        sessionCredits &&
                        sessionCredits.planType !== 'free' &&
                        sessionCredits.planType?.toLowerCase() === plan.id
                      }
                    />
                  )
                })}
              </SimpleGrid>
            </Box>

            {/* Show Session Exchange if switching between non-free plans */}
            {(() => {
              const conversion = calculateSessionConversion()

              if (conversion) {
                return (
                  <Box mb="8">
                    <SessionExchange
                      currentPlan={conversion.currentPlan}
                      currentSessions={conversion.currentSessions}
                      currentFreeCredits={conversion.currentFreeCredits}
                      newPlan={conversion.newPlan}
                      convertedSessions={conversion.convertedSessions}
                      convertedFreeCredits={conversion.convertedFreeCredits}
                    />
                  </Box>
                )
              }
              return null
            })()}

            {/* Step 2: How many sessions */}
            <Box>
              <Flex
                align="center"
                gap={{ base: '2', md: '3' }}
                mb={{ base: '4', md: '6' }}
              >
                <Box
                  w={{ base: '20px', md: '24px' }}
                  h={{ base: '20px', md: '24px' }}
                  bg="#F6F7FE"
                  color="#2D54E8"
                  marginRight={{ base: '8px', md: '10px' }}
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize={{ base: '12px', md: '14px' }}
                  fontWeight="bold"
                  flexShrink={0}
                >
                  2
                </Box>
                <Heading
                  size="md"
                  fontSize={{ base: '16px', md: '18px' }}
                  fontWeight="600"
                  color="black"
                >
                  {calculateSessionConversion()
                    ? 'Purchase additional sessions?'
                    : 'How many sessions?'}
                </Heading>
              </Flex>
              <Box>
                <VStack spacing="4" align="flex-start" w="100%">
                  <Box w="100%">
                    <Input
                      value={sessionCount ?? ''}
                      onChange={e => {
                        const value = e.target.value
                        if (value === '') {
                          setSessionCount(undefined)
                        } else {
                          setSessionCount(Number(value))
                        }
                      }}
                      w={{ base: '100%', sm: '292px' }}
                      type="number"
                      min="30"
                      placeholder="Enter number of sessions"
                      size="md"
                      border="1px solid"
                      borderColor="gray.300"
                    />
                    {sessionCount !== undefined &&
                      sessionCount < 30 &&
                      sessionCount > 0 && (
                        <Text fontSize="12px" color="red.500" mt="1">
                          Minimum purchase is 30 sessions
                        </Text>
                      )}
                  </Box>
                  <Box
                    w="100%"
                    maxW="100%"
                    overflowX={{ base: 'auto', md: 'visible' }}
                    pb={{ base: '2', md: '0' }}
                  >
                    <HStack spacing="2" wrap={{ base: 'nowrap', md: 'wrap' }}>
                      <Button
                        variant={sessionCount === 30 ? 'solid' : 'outline'}
                        colorScheme="blue"
                        size="sm"
                        onClick={() => {
                          setSessionCount(30)
                        }}
                        bg={sessionCount === 30 ? '#2D54E8' : 'white'}
                        color={sessionCount === 30 ? 'white' : '#282828'}
                        borderColor={
                          sessionCount === 30 ? '#2D54E8' : 'pale_gray'
                        }
                        borderWidth="1px"
                        _hover={{
                          bg: sessionCount === 30 ? '#2D54E8' : 'gray.50'
                        }}
                        _focus={{ outline: 'none' }}
                        fontSize={{ base: '12px', md: '14px' }}
                        whiteSpace="nowrap"
                        m="0"
                      >
                        30 sessions
                      </Button>
                      <Button
                        variant={sessionCount === 50 ? 'solid' : 'outline'}
                        colorScheme="blue"
                        size="sm"
                        onClick={() => {
                          setSessionCount(50)
                        }}
                        bg={sessionCount === 50 ? '#2D54E8' : 'white'}
                        color={sessionCount === 50 ? 'white' : '#282828'}
                        borderColor={
                          sessionCount === 50 ? '#2D54E8' : 'pale_gray'
                        }
                        borderWidth="1px"
                        _hover={{
                          bg: sessionCount === 50 ? '#2D54E8' : 'gray.50'
                        }}
                        _focus={{ outline: 'none' }}
                        fontSize={{ base: '12px', md: '14px' }}
                        whiteSpace="nowrap"
                        m="0"
                      >
                        50 sessions
                      </Button>
                      <Button
                        variant={sessionCount === 100 ? 'solid' : 'outline'}
                        colorScheme="blue"
                        size="sm"
                        onClick={() => {
                          setSessionCount(100)
                        }}
                        bg={sessionCount === 100 ? '#2D54E8' : 'white'}
                        color={sessionCount === 100 ? 'white' : '#282828'}
                        borderColor={
                          sessionCount === 100 ? '#2D54E8' : 'pale_gray'
                        }
                        borderWidth="1px"
                        _hover={{
                          bg: sessionCount === 100 ? '#2D54E8' : 'gray.50'
                        }}
                        _focus={{ outline: 'none' }}
                        fontSize={{ base: '12px', md: '14px' }}
                        whiteSpace="nowrap"
                        m="0"
                      >
                        100 sessions
                      </Button>
                      <Button
                        variant={sessionCount === 200 ? 'solid' : 'outline'}
                        colorScheme="blue"
                        size="sm"
                        onClick={() => {
                          setSessionCount(200)
                        }}
                        bg={sessionCount === 200 ? '#2D54E8' : 'white'}
                        color={sessionCount === 200 ? 'white' : '#282828'}
                        borderColor={
                          sessionCount === 200 ? '#2D54E8' : 'pale_gray'
                        }
                        borderWidth="1px"
                        _hover={{
                          bg: sessionCount === 200 ? '#2D54E8' : 'gray.50'
                        }}
                        _focus={{ outline: 'none' }}
                        fontSize={{ base: '12px', md: '14px' }}
                        whiteSpace="nowrap"
                        m="0"
                      >
                        200 sessions
                      </Button>
                    </HStack>
                  </Box>
                </VStack>
                <AutoReplenish
                  sessionCount={sessionCount}
                  propAutoReplenishEnabled={localAutoReplenishEnabled}
                  propAutoReplenishThreshold={localAutoReplenishThreshold}
                  propAutoReplenishCount={localAutoReplenishCount}
                  onAutoReplenishChange={handleAutoReplenishChange}
                />
              </Box>
            </Box>

            {/* Step 3: Payment details - Always show */}
            <Box>
              <Flex
                align="center"
                gap={{ base: '2', md: '3' }}
                mb={{ base: '4', md: '6' }}
              >
                <Box
                  w={{ base: '20px', md: '24px' }}
                  h={{ base: '20px', md: '24px' }}
                  marginRight={{ base: '8px', md: '10px' }}
                  bg="#F6F7FE"
                  color="#2D54E8"
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize={{ base: '12px', md: '14px' }}
                  fontWeight="bold"
                  flexShrink={0}
                >
                  3
                </Box>
                <Heading
                  size="md"
                  fontSize={{ base: '16px', md: '18px' }}
                  fontWeight="600"
                  color="black"
                >
                  Payment details
                </Heading>
              </Flex>
              <Box>
                {selectedPaymentMethodId && defaultPaymentMethod ? (
                  <SavedPaymentMethod
                    paymentMethod={defaultPaymentMethod}
                    onChange={() => setSelectedPaymentMethodId(null)}
                  />
                ) : (
                  <CreditCardForm
                    onComplete={(isComplete: boolean) =>
                      setIsPaymentMethodComplete(isComplete)
                    }
                    onRevertToSaved={
                      defaultPaymentMethod
                        ? () =>
                            setSelectedPaymentMethodId(defaultPaymentMethod.id)
                        : undefined
                    }
                  />
                )}
              </Box>
            </Box>
          </VStack>
        </Box>

        {/* Purchase Summary */}
        <Box
          id="purchase-summary"
          w={{ base: '100%', md: '35%', lg: '35%' }}
          maxW={{ base: '100%', md: '400px', lg: '450px' }}
          flexShrink={0}
          mt={{ base: '6', md: '0' }}
          bg={{ base: 'white', md: 'transparent' }}
          borderRadius={{ base: 'lg', md: 'none' }}
        >
          <PurchaseSummary
            currentPlan={getCurrentPlan()}
            newPlan={getSelectedPlanTitle()}
            sessions={sessionCount ?? 0}
            discount={0}
            pricePerSession={getSelectedPlanPrice()}
            onPurchase={handlePurchase}
            isSubmitting={isSubmitting}
            autoReplenish={localAutoReplenishEnabled}
            autoReplenishThreshold={localAutoReplenishThreshold}
            autoReplenishCount={localAutoReplenishCount}
            isValid={(() => {
              const hasConversion = calculateSessionConversion() !== null
              const hasAdditionalPurchase = sessionCount && sessionCount >= 30
              const hasPaymentMethod = !!(
                selectedPaymentMethodId || isPaymentMethodComplete
              )

              // Valid if:
              // 1. Only conversion (no payment needed)
              // 2. Only purchase with payment method and sessions >= 30
              // 3. Both conversion and purchase with payment method and sessions >= 30
              if (hasConversion && !sessionCount) return true // Only conversion
              if (hasAdditionalPurchase && hasPaymentMethod) return true
              return false
            })()}
            isConversion={calculateSessionConversion() !== null}
            conversionInfo={calculateSessionConversion()}
          />
        </Box>
      </Flex>
    </Box>
  )
}

export const ManagePlan = () => {
  const options = {
    fonts: [
      {
        family: 'TofinoPersonal',
        src: 'url(https://res.cloudinary.com/hellojoy/raw/upload/v1631114007/Fonts/tofino-personal/TofinoPersonal-Regular.otf)'
      }
    ]
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <ManagePlanContent />
    </Elements>
  )
}
